import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "@/lib/router-compat";
import { useLoaderData } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { format } from "date-fns";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image_url: string | null;
  published_at: string | null;
}

const Blog = () => {
  const [searchParams] = useSearchParams();
  const loaderData = useLoaderData({ from: "/blog/", structuralSharing: false }) as
    | { posts: BlogPost[] }
    | undefined;
  const initialPosts = loaderData?.posts ?? [];
  const [posts, setPosts] = useState<BlogPost[]>(initialPosts);
  const [loading, setLoading] = useState(initialPosts.length === 0);

  // Strip legacy Wix query params (?_page=, ?requestId=) by redirecting to clean /blog
  const hasLegacyParams = Array.from(searchParams.keys()).some(
    (k) => k === "requestId" || k.endsWith("_page")
  );

  useEffect(() => {
    if (hasLegacyParams || initialPosts.length > 0) return;
    const fetchPosts = async () => {
      const { data } = await supabase
        .from("published_cms_posts")
        .select("id, title, slug, excerpt, featured_image_url, published_at")
        .eq("status", "published")
        .eq("type", "blog")
        .order("published_at", { ascending: false });
      setPosts(data || []);
      setLoading(false);
    };
    fetchPosts();
  }, [hasLegacyParams, initialPosts.length]);


  if (hasLegacyParams) {
    return (
      <>
        <Navigate to="/blog" replace />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 py-16">
        <h1 className="mb-4 text-center text-4xl font-extrabold text-foreground md:text-5xl">
          GoHub Blog
        </h1>
        <p className="mx-auto mb-6 max-w-2xl text-center text-lg text-muted-foreground">
          Practical insights for online retailers, pharmacies, opticians, and eyewear brands selling prescription glasses online. We cover virtual try-on, AI-guided prescription flows, optical lab fulfillment, pupillary distance measurement, and the operational decisions behind a working Rx eyewear store. New articles every few weeks.
        </p>
        <p className="mx-auto mb-12 max-w-2xl text-center text-sm text-muted-foreground">
          Explore the platform:{" "}
          <Link to="/virtual-try-on" className="text-primary hover:underline">Virtual Try-On & PD measurement</Link>
          {" · "}
          <Link to="/pharmacy" className="text-primary hover:underline">For pharmacies</Link>
          {" · "}
          <Link to="/brands-resellers" className="text-primary hover:underline">For brands & resellers</Link>
        </p>

        {loading ? (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-80 animate-pulse rounded-xl bg-muted" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <p className="text-center text-muted-foreground">No posts yet. Check back soon!</p>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:shadow-lg"
              >
                {post.featured_image_url ? (
                  <img
                    src={post.featured_image_url}
                    alt={post.title}
                    className="h-48 w-full object-cover" loading="lazy" decoding="async" />
                ) : (
                  <div className="flex h-48 items-center justify-center bg-muted text-sm text-muted-foreground">
                    Blog image placeholder
                  </div>
                )}
                <div className="p-6">
                  {post.published_at && (
                    <span className="mb-2 block text-xs text-muted-foreground">
                      {format(new Date(post.published_at), "MMM d, yyyy")}
                    </span>
                  )}
                  <h2 className="mb-2 text-lg font-bold text-card-foreground transition-colors group-hover:text-primary">
                    {post.title}
                  </h2>
                  {post.excerpt && (
                    <p className="text-sm text-muted-foreground line-clamp-3">{post.excerpt}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
